/**
 * Datepickerer – custom element for date/time selection with disabled days support.
 */

const DATE_FORMAT = 'dd/mm/yyyy';
const MAX_PAST_YEARS = 1;
const MAX_FUTURE_YEARS = 2;

class Datepickerer extends HTMLElement {
  constructor() {
    super();
    this._boundResetTime = () => this._resetTimeAndRefresh();
  }

  connectedCallback() {
    this._jsonData = JSON.parse(this.querySelector('[data-disabled-days]').textContent);
    this._dateInput = this.querySelector('#custom-datepicker');
    this._timeSelect = this.querySelector('#custom-timepicker');
    this._timeInput = this.querySelector('input[name^="attributes["][type="time"]');
    this.init();
  }

  disconnectedCallback() {
    this._dateInput?.removeEventListener('changeDate', this._boundResetTime);
    this._dateInput && (this._dateInput.value = '');
    this._timeSelect && (this._timeSelect.value = '');
    this._timeInput && (this._timeInput.value = '');

    this.querySelector('.datepicker').remove();
  }

  // ─── Date helpers ─────────────────────────────────────────────────────────

  _formatDate(date) {
    const d = date.getDate();
    const m = date.getMonth() + 1;
    return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${date.getFullYear()}`;
  }

  _parseDateStr(str) {
    const [dd, mm, yyyy] = str.split('/').map(Number);
    return new Date(yyyy, mm - 1, dd);
  }

  _isDateDisabled(date, disabledDays, disabledDates) {
    if (disabledDays.includes(date.getDay())) return true;
    return disabledDates.includes(this._formatDate(date));
  }

  _getTodayStart() {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }

  _getPastDatesToDisable() {
    const today = this._getTodayStart();
    const disabled = [];
    const start = new Date(today.getFullYear() - MAX_PAST_YEARS, today.getMonth(), 1);
    const date = new Date(today.getFullYear(), today.getMonth(), 1);

    while (date < today && date >= start) {
      disabled.push(this._formatDate(date));
      date.setDate(date.getDate() + 1);
    }
    return disabled;
  }

  _getNextAvailableDate(disabledDays, disabledDates) {
    const date = this._getTodayStart();
    const max = new Date(date.getFullYear() + MAX_FUTURE_YEARS, date.getMonth(), date.getDate());

    while (date <= max) {
      if (!this._isDateDisabled(date, disabledDays, disabledDates)) return new Date(date);
      date.setDate(date.getDate() + 1);
    }
    return null;
  }

  // ─── Time helpers ─────────────────────────────────────────────────────────

  _parseTime(str) {
    if (!str || typeof str !== 'string') return null;
    const [h, m = 0] = str.trim().split(':').map((n) => parseInt(n, 10));
    return Number.isNaN(h) ? null : { hours: h, minutes: m };
  }

  _updateTimeOptions(selectedDateStr) {
    if (!this._timeSelect || !selectedDateStr) return null;

    const [dd, mm, yyyy] = selectedDateStr.split('/').map(Number);
    const selected = new Date(yyyy, mm - 1, dd);
    const today = this._getTodayStart();
    const now = new Date();
    const isToday = selected.getTime() === today.getTime();
    const isPast = selected < today;

    let firstEnabled = null;

    for (const opt of this._timeSelect.options) {
      if (opt.value === '') {
        opt.disabled = false;
        continue;
      }
      const { hours, minutes } = this._parseTime(opt.value) ?? {};
      if (hours == null) {
        opt.disabled = false;
        continue;
      }
      const optTime = new Date(yyyy, mm - 1, dd, hours, minutes, 0, 0);
      opt.disabled = isPast || (isToday && optTime <= now);
      if (!opt.disabled && firstEnabled === null) firstEnabled = opt.value;
    }
    return firstEnabled;
  }

  _resetTimeAndRefresh() {
    if (!this._timeSelect || !this._dateInput?.value) return;
    const first = this._updateTimeOptions(this._dateInput.value);
    this._timeSelect.value = first ?? '';
  }

  // ─── Init ────────────────────────────────────────────────────────────────

  init() {
    if (!this._dateInput) return;

    const disabledDays = this._jsonData.disableDays.map(Number);
    const disabledDates = [
      ...new Set([
        ...this._jsonData.disableSpecificDates.map(String),
        ...this._jsonData.disabledCurrentDate.map(String),
        ...this._getPastDatesToDisable()
      ])
    ];

    let initialDate = this._getTodayStart();
    if (this._isDateDisabled(initialDate, disabledDays, disabledDates)) {
      const next = this._getNextAvailableDate(disabledDays, disabledDates);
      if (next) initialDate = next;
    }

    const minDate = this._getTodayStart();
    const datepicker = new Datepicker(this._dateInput, {
      format: DATE_FORMAT,
      daysOfWeekDisabled: disabledDays,
      weekStart: 1,
      datesDisabled: disabledDates,
      autohide: true,
      minDate
    });
    datepicker.setDate(initialDate);

    // Time select: sync with date changes
    if (this._timeSelect) {
      this._updateTimeOptions(this._formatDate(initialDate));
      const first = Array.from(this._timeSelect.options).find((o) => !o.disabled && o.value);
      if (first) this._timeSelect.value = first.value;

      this._dateInput.addEventListener('changeDate', () => {
        requestAnimationFrame(() => {
          this._boundResetTime();
          this._dateInput.blur();
        });
      });
    }

    // Legacy native time input: block past times when date is today
    if (this._timeInput) {
      const todayStr = this._formatDate(new Date());
      this._timeInput.addEventListener('input', () => {
        if (this._dateInput.value !== todayStr) return;
        const [h, m] = this._timeInput.value.split(':').map(Number);
        if (h == null || m == null) return;
        const selected = new Date();
        selected.setHours(h, m, 0, 0);
        if (selected < new Date()) {
          this._timeInput.value = new Date().toTimeString().slice(0, 5);
        }
      });
    }
  }
}

customElements.define('date-picker', Datepickerer);
